import { z } from 'zod';

/**
 * School version visibility (DECISIONS 2026-10-09 #7). Each school reports what it runs on the
 * signed /private/v1/status poll; central compares it with the latest release and the Host
 * Admin console flags outdated schools. Updates themselves stay manual in Phase 1.
 */

/** Longest version label a school may report (image tag + short digest fits easily). */
export const VERSION_LABEL_MAX = 128;

/** Free-form version label: a release, an image tag, or tag@sha256:<12 hex>. Shown, never parsed. */
export const versionLabelSchema = z
  .string()
  .trim()
  .min(1)
  .max(VERSION_LABEL_MAX)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:@+-]*$/, 'Expected a version or image tag');

const IDENT = '(?:0|[1-9]\\d*|\\d*[A-Za-z-][0-9A-Za-z-]*)';
const RELEASE = new RegExp(
  `^v?(0|[1-9]\\d*)\\.(0|[1-9]\\d*)\\.(0|[1-9]\\d*)(?:-(${IDENT}(?:\\.${IDENT})*))?(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?$`,
);

export interface ReleaseVersion {
  readonly core: readonly [number, number, number];
  /** Pre-release identifiers (numbers stay numbers for SemVer precedence). */
  readonly pre: readonly (string | number)[];
}

/** Parses a SemVer 2.0 version with an optional leading "v". Null when malformed. */
export function parseReleaseVersion(raw: string): ReleaseVersion | null {
  const match = RELEASE.exec(raw.trim());
  if (!match) return null;
  const pre = match[4] ? match[4].split('.').map((id) => (/^\d+$/.test(id) ? Number(id) : id)) : [];
  return { core: [Number(match[1]), Number(match[2]), Number(match[3])], pre };
}

/** A SemVer release (v prefix, pre-release and build metadata allowed). */
export const releaseVersionSchema = z
  .string()
  .trim()
  .max(VERSION_LABEL_MAX)
  .refine((v) => parseReleaseVersion(v) !== null, 'Expected a release version like 1.2.3');

type Order = -1 | 0 | 1;
const sign = (n: number): Order => (n === 0 ? 0 : n > 0 ? 1 : -1);

function compareIdentifier(a: string | number, b: string | number): Order {
  if (typeof a === 'number' && typeof b === 'number') return sign(a - b);
  if (typeof a === 'number') return -1;
  if (typeof b === 'number') return 1;
  return a === b ? 0 : a < b ? -1 : 1;
}

function comparePre(a: ReleaseVersion['pre'], b: ReleaseVersion['pre']): Order {
  if (a.length === 0 || b.length === 0) return sign(b.length - a.length);
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    const order = compareIdentifier(a[i] as string | number, b[i] as string | number);
    if (order !== 0) return order;
  }
  return sign(a.length - b.length);
}

/** SemVer precedence (build metadata ignored). Null when either side is malformed. */
export function compareReleaseVersions(a: string, b: string): Order | null {
  const pa = parseReleaseVersion(a);
  const pb = parseReleaseVersion(b);
  if (!pa || !pb) return null;
  for (let i = 0; i < 3; i += 1) {
    const order = sign(pa.core[i]! - pb.core[i]!);
    if (order !== 0) return order;
  }
  return comparePre(pa.pre, pb.pre);
}

export const versionStatusSchema = z.enum(['current', 'outdated', 'ahead', 'unknown']);
export type VersionStatus = z.infer<typeof versionStatusSchema>;

/** Where a school stands against the latest release. Unknown if either is missing or malformed. */
export function schoolVersionStatus(running: string | null, latest: string | null): VersionStatus {
  if (running === null || latest === null) return 'unknown';
  const order = compareReleaseVersions(running, latest);
  if (order === null) return 'unknown';
  return order < 0 ? 'outdated' : order > 0 ? 'ahead' : 'current';
}

/** Last versions a school reported (kept when later polls fail). */
export const tenantVersionsSchema = z.object({
  tenantApi: versionLabelSchema.nullable(),
  keycloak: versionLabelSchema.nullable(),
  /** TP_RELEASE_VERSION from the school's .env (written by install/update). */
  release: versionLabelSchema.nullable(),
  reportedAt: z.iso.datetime(),
});
export type TenantVersions = z.infer<typeof tenantVersionsSchema>;

/** The version compared with the latest release: the release, else the tenant-api version. */
export function runningVersion(versions: TenantVersions | null): string | null {
  return versions ? (versions.release ?? versions.tenantApi) : null;
}

/** Optional status field from a school: a missing or bad label is null, never a failed poll. */
export const reportedVersionLabelSchema = versionLabelSchema.nullable().optional().catch(null);
